export type ParsedAddress = {
  readonly street: string;
  readonly houseNumber: string;
  readonly postalCode: string;
  readonly city: string;
  readonly country: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
};

// Shape of an addressComponent returned by the new Places API (Place class).
// Legacy `address_components[i].long_name` is `addressComponents[i].longText`.
type AddressComponent = {
  readonly longText?: string | null;
  readonly shortText?: string | null;
  readonly types?: ReadonlyArray<string>;
};

type LatLng = {
  readonly lat: () => number;
  readonly lng: () => number;
};

// Shape of the new `Place` instance after `place.fetchFields({ fields: [...] })`.
type Place = {
  readonly addressComponents?: ReadonlyArray<AddressComponent> | null;
  readonly location?: LatLng | null;
};

function pick(components: ReadonlyArray<AddressComponent>, type: string): string {
  return components.find((c) => c.types?.includes(type))?.longText ?? '';
}

function pickCoordinate(reader: (() => number) | undefined): number | null {
  if (typeof reader !== 'function') return null;

  const value = reader();

  return Number.isFinite(value) ? value : null;
}

export function parsePlace(place: Place): ParsedAddress {
  const components = place.addressComponents ?? [];
  const location = place.location ?? undefined;

  return {
    street: pick(components, 'route'),
    houseNumber: pick(components, 'street_number'),
    postalCode: pick(components, 'postal_code'),
    city:
      pick(components, 'locality') ||
      pick(components, 'postal_town') ||
      pick(components, 'administrative_area_level_2'),
    country: pick(components, 'country'),
    latitude: pickCoordinate(location?.lat),
    longitude: pickCoordinate(location?.lng),
  };
}
